import * as React from "react";
import { OverlayTrigger, Popover } from "react-bootstrap";
import styles from "./AvailableOfficeWindows.module.scss";
import {
    AgeCategory,
    DateFormats,
    IClinician,
    IOfficeWindow,
    IOfficeWindowSchedule,
    ISearchParams,
    OfficeMangerOfficeWindow,
    TOffices,
} from "intake_shared/src/types";
import store from "../../../../store";
import { Dropdown } from "../../bits/Dropdown/Dropdown";
import { Button } from "../../bits/Button/Button";
import ChevronDownIcon from "../../icons/ChevronDown";
import { AclContext } from "../../../../contexts/AclContext";
import { useToasts } from "react-toast-notifications";
import { cloneDeep, uniq } from "lodash";
import moment from "moment";
import { v4 as uuidv4 } from "uuid";
import {
    findAvailableTimeSlotsClinicianCanBook,
    findMatchingSchedulesForClinicians,
    getStringFromScheduleDate,
    getTimesUsedByClinicianForTheSelectedDay,
    getTimesUsedByOtherClincians,
    handleFormatDatePickerValue,
    hasConflictingAvailability,
    hasConflictingAvailabilityWithCliniciansRemoteSchedules,
    isSameBooking,
    sortStringsWithNumberEndings,
} from "../../../../utils/utils";
import { fetchCliniciansByIds } from "../../../../store/clinician";
import { TableHeader } from "../../../../types";
import { Loader } from "../../bits/Loader/Loader";
import TablePagination from "../../bits/Table/TablePagination";
import FilterButton from "../../bits/FilterButton/FilterButton";
import Table from "../../bits/Table/Table";
import RequestOfficeForm from "../../clinicians/RequestOfficeWindow/RequestOfficeForm/RequestOfficeForm";
import { Dialog } from "../../bits/Dialog/Dialog";
import { Select } from "../../bits/Select/Select";
import { DatePicker } from "../../bits/DatePicker/DatePicker";
import { TextArea } from "../../bits/TextArea/TextArea";
import EmptyListState from "../../bits/EmptyListState/EmptyListState";

const defaultSearchParams: ISearchParams = {
    available_days_only: true,
    first_name: "",
    age_categories: [] as AgeCategory[],
    methodology: {},
    experience_level: {},
    search_locations: [],
    search_credentials: [],
    experience_level_filter: {},
    show_only_available: false,
    available_services: [],
    priority_marked: false,
};

interface MatchingClinician {
    clinicianId: string;
    schedules: IOfficeWindow[];
}

interface DeleteOfficeWindowImpactData {
    officeWindowId: string;
    clinicians: IClinician[];
    remainingOfficeWindows: IOfficeWindow[];
}

interface BlockOfficeProps {
    blocked_reason: string;
    start_date: Date;
    end_date: Date | null;
    clinician_email?: string;
}

interface AvailableOfficeWindowsProps {
    officeManagerLocations: string[] | undefined;
    filteredOffices: TOffices;
}

export default function AvailableOfficeWindows({
    officeManagerLocations,
    filteredOffices,
}: AvailableOfficeWindowsProps) {
    const [actionRow, setActionRow] =
        React.useState<OfficeMangerOfficeWindow | null>(null);
    const [requestOfficeDetails, setRequestOfficeDetails] = React.useState({
        show: false,
    });

    const loggedInUserEmail = React.useContext(AclContext)?.authUser.user?.email;
    const { addToast } = useToasts();
    const [currentPage, setCurrentPage] = React.useState(1);
    const pageLimit = 50;
    const [loading, setLoading] = React.useState(false);
    const [foundClinicians, setFoundClincians] = React.useState([] as IClinician[]);
    const [nextButtonDisabled, setNextButtonDisabled] = React.useState(false);
    const [refreshKey, setRefreshKey] = React.useState(0);
    const [officeDetailsToBlock, setOfficeDetailsToBlock] =
        React.useState<BlockOfficeProps>({
            start_date: new Date(),
            blocked_reason: "",
            end_date: null,
            clinician_email: "",
        } as BlockOfficeProps);
    const [existingSchedules, setExistingSchedules] = React.useState(
        [] as IOfficeWindowSchedule[]
    );
    const [officeWindows, setOfficeWindows] =
        React.useState<OfficeMangerOfficeWindow[]>();
    const [deleteOfficeWindow, setDeleteOfficeWindow] = React.useState(false);
    const [blockOffice, setBlockOffice] = React.useState(false);
    const [clinicianList, setClinicianList] = React.useState<IClinician[]>([]);
    const [selectedLocation, setSelectedLocation] = React.useState("");
    const [selectedOfficeNumber, setSelectedOfficeNumber] = React.useState("");
    const [reasonForDeleting, setReasonForDeleting] = React.useState("");
    const [officeWindowToDelete, setSelectedOfficeWindowToDelete] =
        React.useState<OfficeMangerOfficeWindow>({} as OfficeMangerOfficeWindow);
    const [slotsAvailableForClinician, setSlotsAvailableForClinician] =
        React.useState<string[]>();
    const [searchParams] = React.useState<ISearchParams>(
        cloneDeep(defaultSearchParams)
    );
    const [matchingDeleteClinicians, setMatchingDeleteClinicians] = React.useState<
        { name: string; email: string; schedules: IOfficeWindow[] }[]
    >([]);
    const [deleteOfficeWindowImpactData, setDeleteOfficeWindowImpactData] =
        React.useState<DeleteOfficeWindowImpactData | null>(null);
    const [loadingMatchingClinicians, setLoadingMatchingClinicians] =
        React.useState(false);
    const [matchingCliniciansError, setMatchingCliniciansError] =
        React.useState(false);

    const loadDeleteOfficeWindowImpactData = async (
        row: OfficeMangerOfficeWindow
    ) => {
        const [rawClinicians, officeWindowsForOffice] = await Promise.all([
            store.fetchAllCliniciansByLocation(row.location),
            store.getPotentialCoveringOfficeWindows(
                loggedInUserEmail as string,
                row
            ),
        ]);

        return {
            officeWindowId: row.id,
            clinicians: rawClinicians || [],
            remainingOfficeWindows: (officeWindowsForOffice || []).filter(
                (w) => w.id !== row.id
            ),
        };
    };

    const getMatchingCliniciansForOfficeWindow = (
        row: OfficeMangerOfficeWindow,
        clinicians: IClinician[]
    ) => {
        const matches: MatchingClinician[] = [];

        clinicians.forEach((clinician) => {
            const matchingSchedules = clinician.office_windows.filter((schedule) =>
                isSameBooking(schedule, row)
            );

            if (matchingSchedules.length === 0) return;

            matches.push({
                clinicianId: clinician.email,
                schedules: matchingSchedules,
            });
        });

        return matches;
    };

    const excludeSchedulesCoveredByRemainingOfficeWindows = (
        matchingClinicians: MatchingClinician[],
        remainingOfficeWindows: IOfficeWindow[]
    ) => {
        return matchingClinicians
            .map((match) => ({
                ...match,
                schedules: match.schedules.filter(
                    (schedule) =>
                        !remainingOfficeWindows.some((gw) =>
                            isSameBooking(schedule, gw)
                        )
                ),
            }))
            .filter((match) => match.schedules.length > 0);
    };

    const fetchMatchingClinicianDetails = async (row: OfficeMangerOfficeWindow) => {
        setLoadingMatchingClinicians(true);
        setMatchingCliniciansError(false);
        setDeleteOfficeWindowImpactData(null);
        try {
            const impactData = await loadDeleteOfficeWindowImpactData(row);
            setDeleteOfficeWindowImpactData(impactData);

            const matches = excludeSchedulesCoveredByRemainingOfficeWindows(
                getMatchingCliniciansForOfficeWindow(row, impactData.clinicians),
                impactData.remainingOfficeWindows
            );

            setMatchingDeleteClinicians(
                matches
                    .map((m) => {
                        const clinician = impactData.clinicians.find(
                            (c) => c.email === m.clinicianId
                        );
                        return clinician
                            ? {
                                  name: `${clinician.firstname} ${clinician.lastname}`,
                                  email: clinician.email,
                                  schedules: m.schedules,
                              }
                            : null;
                    })
                    .filter(Boolean) as {
                    name: string;
                    email: string;
                    schedules: IOfficeWindow[];
                }[]
            );
        } catch {
            setMatchingCliniciansError(true);
            setMatchingDeleteClinicians([]);
        } finally {
            setLoadingMatchingClinicians(false);
        }
    };

    const getAvailableOfficeWindows = async () => {
        const filters = {
            location: selectedLocation,
            office: selectedOfficeNumber,
        };
        setLoading(true);
        try {
            const requests = await store.getOfficeWindows(
                loggedInUserEmail as string,
                "get_available_office_windows",
                currentPage,
                pageLimit,
                filters
            );
            setOfficeWindows(requests);
            setLoading(false);

            // Check if there is more data for the next page
            setNextButtonDisabled(requests.length < pageLimit);

            // Get the current date using moment
            const currentDate = moment();

            // Iterate through the requests to find blocked requests with an end_date that has passed
            for (const request of requests) {
                if (
                    request.blocked &&
                    moment(request.end_date).isBefore(currentDate)
                ) {
                    // Unblock the request and reset start_date, end_date, and blocked_reason
                    const updatedRequest = {
                        ...request,
                        blocked: false,
                        start_date: "",
                        end_date: "",
                        blocked_reason: "",
                    };
                    // Unblock the request
                    const success = await store.blockOrUnblockOfficeWindowById(
                        request.id,
                        false,
                        updatedRequest
                    );

                    if (success) {
                        setOfficeWindows((prevState) =>
                            prevState?.map((r) =>
                                r.id === request.id ? updatedRequest : r
                            )
                        );
                    }
                }
            }
        } catch (error) {
            // no need to return toast here since we are already returning on the first list being office request list
            setLoading(false);
        }
    };

    const validOfficeWindows = officeWindows?.filter((row) => {
        const validOffices = filteredOffices[row.location];
        if (validOffices === undefined) return true;
        return validOffices.includes(row.office);
    });

    const handleDeleteOfficeWindow = async (
        officeWindowToDelete: OfficeMangerOfficeWindow
    ) => {
        const impactData =
            deleteOfficeWindowImpactData?.officeWindowId === officeWindowToDelete.id
                ? deleteOfficeWindowImpactData
                : await loadDeleteOfficeWindowImpactData(officeWindowToDelete);
        const clinicianList = impactData.clinicians;

        // get all clinician for that particular location
        clinicianList.forEach((c) => {
            let schedules: IOfficeWindowSchedule[] = [];
            schedules = schedules.concat(
                c.office_windows
                    .filter((w) => w.location === officeWindowToDelete.location)
                    .map((w) => {
                        return {
                            clinicianId: c.email,
                            id: w.id,
                            assigned_to: `${c.firstname} ${c.lastname}`,
                            start: w.start,
                            end: w.end,
                            ending_date: w.ending_date,
                            office: w.office,
                            location: officeWindowToDelete.location,
                        };
                    })
            );
            return schedules;
        });

        const allMatchingClinicians = getMatchingCliniciansForOfficeWindow(
            officeWindowToDelete,
            clinicianList
        );
        const matchingClinicians = excludeSchedulesCoveredByRemainingOfficeWindows(
            allMatchingClinicians,
            impactData.remainingOfficeWindows
        );

        const getMatchingCliniciansIds = matchingClinicians.map(
            (clinician) => clinician.clinicianId
        );

        const matchedCliniciansDetails = await fetchCliniciansByIds(
            getMatchingCliniciansIds
        );

        const filteredSchedule = await findMatchingSchedulesForClinicians(
            matchedCliniciansDetails,
            matchingClinicians
        );

        // For each clinician, drop availability slots still covered by a remaining window
        const filteredScheduleWithCoverageCheck = filteredSchedule.map(
            ({ clinicianId, schedules }) => {
                const clinician = clinicianList.find((c) => c.email === clinicianId);
                if (!clinician) return { clinicianId, schedules };

                const deletedWindowIds = new Set(
                    (
                        matchingClinicians.find((m) => m.clinicianId === clinicianId)
                            ?.schedules || []
                    ).map((s) => s.id)
                );
                const remainingCliniciansWindows = clinician.office_windows.filter(
                    (w) => !deletedWindowIds.has(w.id)
                );

                const schedulesToDelete = schedules.filter(
                    (slot) =>
                        !remainingCliniciansWindows.some((rw) =>
                            isSameBooking(slot as unknown as IOfficeWindow, rw)
                        )
                );

                return { clinicianId, schedules: schedulesToDelete };
            }
        );

        //  update the office window of the matching clinicians
        await store.removeClinicianOfficeWindowAndAvailability(
            matchingClinicians,
            filteredScheduleWithCoverageCheck
        );

        const deleted = await store.deleteOfficeWindowById(
            officeWindowToDelete,
            loggedInUserEmail as string,
            reasonForDeleting
        );

        if (deleted) {
            addToast("Office window deleted successfully", {
                appearance: "success",
            });
            setDeleteOfficeWindow(false);
            setRefreshKey((prevKey) => prevKey + 1);
            setReasonForDeleting("");
            setOfficeDetailsToBlock({
                ...officeDetailsToBlock,
                blocked_reason: "",
                start_date: new Date(),
                end_date: null,
            });
        }
    };

    const getClinicianDetails = foundClinicians?.filter(
        (clinician) => clinician.email === officeDetailsToBlock.clinician_email
    );

    React.useEffect(() => {
        if (getClinicianDetails) {
            let schedules: IOfficeWindowSchedule[] = [];
            clinicianList.forEach((c) => {
                schedules = schedules.concat(
                    c.office_windows
                        .filter((w) => w.location === officeWindowToDelete?.location)
                        .map((w) => {
                            return {
                                clinicianId: c.email,
                                id: w.id,
                                assigned_to: `${c.firstname} ${c.lastname}`,
                                start: w.start,
                                end: w.end,
                                ending_date: w.ending_date,
                                office: w.office,
                                location: officeWindowToDelete?.location,
                            };
                        })
                );
            });
            setExistingSchedules(schedules);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [clinicianList, officeWindowToDelete?.location]);

    const fetchCliniciansByLocation = React.useCallback(async (location: string) => {
        setClinicianList((await store.fetchAllCliniciansByLocation(location)) || []);
    }, []);

    React.useEffect(() => {
        getAvailableOfficeWindows();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [refreshKey, currentPage, selectedLocation, selectedOfficeNumber]);

    React.useEffect(() => {
        if (officeWindowToDelete.location) {
            fetchCliniciansByLocation(officeWindowToDelete.location);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [officeWindowToDelete?.location]);

    React.useEffect(() => {
        if (getClinicianDetails) {
            // get all the times that are not available into one array
            const getAllUnAvailableTimes = () => {
                const timesUsedByClinician =
                    getTimesUsedByClinicianForTheSelectedDay(
                        getClinicianDetails[0]?.office_windows,
                        moment(officeWindowToDelete.start).format(DateFormats.DAY)
                    );
                const timesUsedByOtherClinicians = getTimesUsedByOtherClincians(
                    existingSchedules,
                    officeWindowToDelete.location,
                    officeWindowToDelete.office,
                    moment(officeWindowToDelete.start).format(DateFormats.DAY)
                );
                const allUnAvailableTimes = [
                    ...timesUsedByClinician,
                    ...timesUsedByOtherClinicians,
                ];
                return allUnAvailableTimes;
            };
            // this is the time slots that are occupied for that day
            const occupiedTimeSlots = getAllUnAvailableTimes();

            const slotsAvailable =
                findAvailableTimeSlotsClinicianCanBook(occupiedTimeSlots);
            setSlotsAvailableForClinician(slotsAvailable);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [getClinicianDetails.length, officeDetailsToBlock.clinician_email]);

    const handleBlockAction = async (row: OfficeMangerOfficeWindow) => {
        setLoading(true);
        const action = row.blocked ? false : true;
        if (officeDetailsToBlock?.clinician_email !== "") {
            const bookingData = {
                id: uuidv4(),
                clinicianId: officeDetailsToBlock?.clinician_email as string,
                office: row.office,
                location: row.location,
                start: officeWindowToDelete.start,
                end: officeWindowToDelete.end,
                ending_date: `${handleFormatDatePickerValue(
                    officeDetailsToBlock.end_date as unknown as string
                )}T00:00:00.000`,
            };

            const hasConflictWithClinicianSchedule =
                await hasConflictingAvailabilityWithCliniciansRemoteSchedules(
                    getClinicianDetails[0]?.office_windows ||
                        ({} as IOfficeWindow[]),
                    officeWindowToDelete.start,
                    officeWindowToDelete.end
                );
            const hasConflictWithOtherClinicians = hasConflictingAvailability(
                existingSchedules,
                officeWindowToDelete.start,
                officeWindowToDelete.end,
                officeWindowToDelete.office,
                officeWindowToDelete.location,
                officeDetailsToBlock.clinician_email as string
            );
            if (
                !hasConflictWithClinicianSchedule &&
                !hasConflictWithOtherClinicians
            ) {
                await store.addClinicianOfficeWindow(bookingData);
            } else {
                addToast(
                    [
                        `
      Conflicting schedule with ${
          hasConflictWithOtherClinicians?.clinicianId ||
          officeDetailsToBlock.clinician_email
      },`,
                        `${
                            hasConflictWithOtherClinicians?.location ||
                            hasConflictWithClinicianSchedule?.location
                        }, ${
                            hasConflictWithOtherClinicians?.office ||
                            hasConflictWithClinicianSchedule?.office
                        }`,
                        `@ ${moment(
                            getStringFromScheduleDate(
                                hasConflictWithOtherClinicians?.start ||
                                    hasConflictWithClinicianSchedule?.start
                            )
                        ).format(DateFormats.DAY_HOUR_FORMAT)} - ${moment(
                            getStringFromScheduleDate(
                                hasConflictWithOtherClinicians?.end ||
                                    hasConflictWithClinicianSchedule?.end
                            )
                        ).format(DateFormats.DAY_HOUR_FORMAT)}`,
                    ].join(" "),
                    { appearance: "error", autoDismiss: false }
                );
                setLoading(false);
                return;
            }
        }
        const success = await store.blockOrUnblockOfficeWindowById(row.id, action, {
            ...officeDetailsToBlock,
            start_date: `${handleFormatDatePickerValue(
                officeDetailsToBlock.start_date as unknown as string
            )}T00:00:00.000`,
            end_date:
                officeDetailsToBlock.end_date === null
                    ? ""
                    : `${handleFormatDatePickerValue(
                          officeDetailsToBlock.end_date as unknown as string
                      )}T00:00:00.000`,
        });
        if (success) {
            setLoading(false);
            addToast(
                `Office window ${action ? "blocked" : "unblocked"} successfully`,
                {
                    appearance: "success",
                }
            );
            setRefreshKey((prevKey) => prevKey + 1);
        }
        setBlockOffice(false);
        setOfficeDetailsToBlock({
            ...officeDetailsToBlock,
            blocked_reason: "",
            start_date: new Date(),
            end_date: null,
        });
        setReasonForDeleting("");
    };

    const handleResetFilters = () => {
        setSelectedLocation("");
        setSelectedOfficeNumber("");
        setCurrentPage(1);
    };

    const columns = React.useMemo(() => {
        const headers: TableHeader[] = [
            {
                Header: "DAY",
                id: "",
                accessor: (row: OfficeMangerOfficeWindow) => (
                    <p>{`${moment(row.start).format(DateFormats.DAY)}`}</p>
                ),
            },
            {
                Header: "TIME",
                id: "start_date",
                accessor: (row: OfficeMangerOfficeWindow) => (
                    <p>{`${moment(row.start).format(
                        DateFormats.HOUR_MIN_FORMAT
                    )} - ${moment(row.end).format(DateFormats.HOUR_MIN_FORMAT)}`}</p>
                ),
            },
            {
                Header: "LOCATION",
                id: "location",
                accessor: (row: OfficeMangerOfficeWindow) => (
                    <p>{row.location || ""}</p>
                ),
            },
            {
                Header: "OFFICE NO",
                id: "office_number",
                accessor: (row: OfficeMangerOfficeWindow) => (
                    <p>{row.office || ""}</p>
                ),
            },
            {
                Header: "BLOCKED",
                id: "blocked",
                accessor: (row: OfficeMangerOfficeWindow) => (
                    <p>{row.blocked === true ? "Yes" : "No"}</p>
                ),
            },
            {
                Header: "BLOCKED DURATION",
                id: "blocked_from",
                accessor: (row: OfficeMangerOfficeWindow) => (
                    <p>
                        {" "}
                        {row?.start_date
                            ? moment(row?.start_date).format(DateFormats.DATE_FORMAT)
                            : ""}
                        {""} - {""}
                        {row?.end_date
                            ? moment(row?.end_date).format(DateFormats.DATE_FORMAT)
                            : ""}
                    </p>
                ),
            },
            {
                Header: "NOTES",
                id: "notes",
                accessor: (row: OfficeMangerOfficeWindow) => (
                    <div> {row?.blocked_reason || "--"}</div>
                ),
            },

            {
                Header: "Actions",
                id: "actions",
                accessor: (row: OfficeMangerOfficeWindow) => {
                    return (
                        <OverlayTrigger
                            trigger="click"
                            placement="bottom-end"
                            flip
                            rootClose
                            show={actionRow === row}
                            onToggle={(show) => setActionRow(show ? row : null)}
                            overlay={
                                <Popover
                                    id="office-window-row-actions"
                                    className={styles.actionsPopover}
                                    aria-label="Row actions"
                                >
                                    <Popover.Content
                                        className={styles.actionsDropdownContent}
                                        onClick={() => setActionRow(null)}
                                    >
                                        <Button
                                            variant="normal"
                                            type="button"
                                            onClick={() => {
                                                setSelectedOfficeWindowToDelete(row);
                                                row.blocked
                                                    ? handleBlockAction(row)
                                                    : setBlockOffice(true);
                                            }}
                                        >
                                            {row?.blocked === true
                                                ? "Unblock"
                                                : "Block"}
                                        </Button>
                                        <Button
                                            variant="normal"
                                            type="button"
                                            onClick={() => {
                                                setSelectedOfficeWindowToDelete(row);
                                                setDeleteOfficeWindow(true);
                                                fetchMatchingClinicianDetails(row);
                                            }}
                                        >
                                            Delete Office Window
                                        </Button>
                                    </Popover.Content>
                                </Popover>
                            }
                        >
                            <Button
                                aria-expanded={actionRow === row}
                                iconRight={
                                    <ChevronDownIcon stroke="var(--color-white)" />
                                }
                            >
                                Actions
                            </Button>
                        </OverlayTrigger>
                    );
                },
            },
        ];
        return headers;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [actionRow]);

    const handleFirstPageClick = () => {
        setCurrentPage(1);
    };

    const handlePrevClick = () => {
        if (currentPage > 1) {
            setCurrentPage(currentPage - 1);
        }
    };

    const handleNextClick = () => {
        setCurrentPage(currentPage + 1);
    };

    const isRequiredFieldProvided = () => {
        let isValid = true;

        if (deleteOfficeWindow && reasonForDeleting === "") {
            isValid = false;
        }

        if (
            blockOffice &&
            (officeDetailsToBlock.blocked_reason === "" ||
                officeDetailsToBlock.start_date === null)
        ) {
            isValid = false;
        }

        return isValid;
    };

    const options = foundClinicians.map((clinician) => ({
        value: clinician.email,
        label: clinician.firstname + " " + clinician.lastname,
    }));

    const filteredOptions = options.filter((option) =>
        option.label.toLowerCase().includes(searchParams.first_name.toLowerCase())
    );

    const handleSelect = (selected) => {
        setOfficeDetailsToBlock({
            ...officeDetailsToBlock,
            clinician_email: selected.value,
        });
    };

    const tableFooter = (
        <TablePagination
            currentPage={currentPage}
            onFirstPage={handleFirstPageClick}
            onPrevPage={handlePrevClick}
            onNextPage={handleNextClick}
            isNextDisabled={nextButtonDisabled}
            showPrevButton={currentPage > 1}
        />
    );

    const getLocationLabel = (location: string) => {
        return location || "Select location";
    };

    const getOfficeLabel = (office: string) => {
        return office || "Select office";
    };

    const runClinicianSearch = async () => {
        const clinicians = await store.searchClincians(searchParams);
        setFoundClincians(clinicians);
    };

    React.useEffect(() => {
        runClinicianSearch();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <div className={styles.container}>
            <Loader show={loading} />
            <RequestOfficeForm
                requestOfficeDetails={{
                    ...requestOfficeDetails,
                    onHide: () => {
                        setRequestOfficeDetails({
                            ...requestOfficeDetails,
                            show: false,
                        });
                    },
                }}
                setRequestOfficeDetails={setRequestOfficeDetails}
                viewAs="office-manager"
                setRefreshKey={setRefreshKey}
                refreshKey={refreshKey}
            />
            <Dialog
                open={deleteOfficeWindow || blockOffice}
                onClose={() => {
                    setDeleteOfficeWindow(false);
                    setBlockOffice(false);
                    setMatchingDeleteClinicians([]);
                    setDeleteOfficeWindowImpactData(null);
                    setMatchingCliniciansError(false);
                }}
                onCancel={() => {
                    setDeleteOfficeWindow(false);
                    setBlockOffice(false);
                    setMatchingDeleteClinicians([]);
                    setDeleteOfficeWindowImpactData(null);
                    setMatchingCliniciansError(false);
                }}
                title={`${officeWindowToDelete.location} (${
                    officeWindowToDelete.office
                }) ${moment(officeWindowToDelete.start).format(
                    DateFormats.DAY
                )} - ${moment(officeWindowToDelete.start).format(
                    DateFormats.HOUR_MIN_FORMAT
                )} - ${moment(officeWindowToDelete.end).format(
                    DateFormats.HOUR_MIN_FORMAT
                )}`}
                onSave={() => {
                    deleteOfficeWindow
                        ? handleDeleteOfficeWindow(officeWindowToDelete)
                        : handleBlockAction(officeWindowToDelete);
                }}
                saveText={
                    deleteOfficeWindow
                        ? "Delete Office Window"
                        : "Block Office Window"
                }
                isSubmitBtnDisabled={
                    !isRequiredFieldProvided() || loadingMatchingClinicians
                }
                isDeleting
            >
                <div className="v2-form">
                    {blockOffice && (
                        <div>
                            <p className={styles.note}>
                                Blocking this window means clinicians cannot use this
                                office window. Do you want to block this window for a
                                particular clinician? If yes select the clinician
                                from the dropdown, otherwise you can proceed
                            </p>
                            <Select
                                label="Clinician"
                                options={filteredOptions}
                                onChange={(val) => handleSelect(val)}
                                isLongListInDialog
                                isSearchable
                            />
                        </div>
                    )}

                    {getClinicianDetails?.length > 0 &&
                        slotsAvailableForClinician &&
                        slotsAvailableForClinician?.length > 0 && (
                            <div className={styles.availableTime}>
                                Available time on{" "}
                                {moment(officeWindowToDelete.start).format(
                                    DateFormats.DAY
                                )}{" "}
                                you can book for this clinician includes:
                                <p className={styles.availableTimeSlots}>
                                    {slotsAvailableForClinician?.join(", ")}
                                </p>
                            </div>
                        )}

                    <div>
                        {deleteOfficeWindow && (
                            <div className={styles.affectedClinicians}>
                                {loadingMatchingClinicians ? (
                                    <p className={styles.note}>
                                        Loading affected clinicians...
                                    </p>
                                ) : matchingCliniciansError ? (
                                    <p className={styles.note}>
                                        Unable to load affected clinicians.
                                    </p>
                                ) : matchingDeleteClinicians.length > 0 ? (
                                    <>
                                        <p
                                            className={
                                                styles.affectedCliniciansTitle
                                            }
                                        >
                                            The following office window slots will be
                                            permanently removed:
                                        </p>
                                        <ul
                                            className={styles.affectedCliniciansList}
                                        >
                                            {matchingDeleteClinicians.map((c) => (
                                                <div
                                                    className={
                                                        styles.affectedClinician
                                                    }
                                                    key={c.email}
                                                >
                                                    <strong>{c.name}</strong>
                                                    <span
                                                        className={
                                                            styles.clinicianScheduleTime
                                                        }
                                                    >
                                                        {c.schedules
                                                            .map(
                                                                (s) =>
                                                                    `${moment(
                                                                        s.start
                                                                    ).format(
                                                                        DateFormats.HOUR_MIN_FORMAT
                                                                    )} - ${moment(
                                                                        s.end
                                                                    ).format(
                                                                        DateFormats.HOUR_MIN_FORMAT
                                                                    )}`
                                                            )
                                                            .join(", ")}
                                                    </span>
                                                </div>
                                            ))}
                                        </ul>
                                        <p className={styles.note}>
                                            Held and confirmed appointments within
                                            this window will not be removed, but
                                            their time slot will no longer have an
                                            approved office window.
                                        </p>
                                    </>
                                ) : (
                                    <p className={styles.note}>
                                        No open availability slots will be removed
                                        for this window.
                                    </p>
                                )}
                            </div>
                        )}
                        <TextArea
                            label={
                                deleteOfficeWindow
                                    ? "Provide a reason for deleting this office window"
                                    : "Provide a reason for blocking this office window"
                            }
                            isRequired
                            value={
                                officeDetailsToBlock.blocked_reason ||
                                reasonForDeleting
                            }
                            onChange={(e) => {
                                setOfficeDetailsToBlock({
                                    ...officeDetailsToBlock,
                                    blocked_reason: e.target.value,
                                });
                                setReasonForDeleting(e.target.value);
                            }}
                            className={styles.reasonForChanges}
                        />
                    </div>
                    {blockOffice ? (
                        <div className="gridTwo">
                            <DatePicker
                                label="Start Date"
                                selected={officeDetailsToBlock.start_date}
                                minDate={new Date()}
                                onChange={(date) =>
                                    setOfficeDetailsToBlock({
                                        ...officeDetailsToBlock,
                                        start_date: date as Date,
                                    })
                                }
                                required
                            />
                            <DatePicker
                                label="End Date"
                                selected={officeDetailsToBlock.end_date}
                                minDate={new Date()}
                                onChange={(date) =>
                                    setOfficeDetailsToBlock({
                                        ...officeDetailsToBlock,
                                        end_date: date as Date,
                                    })
                                }
                                required
                            />
                        </div>
                    ) : null}
                </div>
            </Dialog>
            <div className={styles.availableOfficeWindowsFilters}>
                <div className={styles.filtersLeft}>
                    <Dropdown>
                        <Dropdown.Trigger>
                            <FilterButton
                                text={getLocationLabel(selectedLocation)}
                            />
                        </Dropdown.Trigger>

                        <Dropdown.Content width="170px">
                            <Dropdown.Item
                                closeMenuOnSelect
                                isSelected={selectedLocation === ""}
                                onClick={() => setSelectedLocation("")}
                            >
                                Show all
                            </Dropdown.Item>
                            {officeManagerLocations?.map((locations) => (
                                <Dropdown.Item
                                    key={locations}
                                    closeMenuOnSelect
                                    isSelected={selectedLocation === locations}
                                    onClick={() => setSelectedLocation(locations)}
                                >
                                    {locations}
                                </Dropdown.Item>
                            ))}
                        </Dropdown.Content>
                    </Dropdown>
                    <Dropdown>
                        <Dropdown.Trigger>
                            <FilterButton
                                text={getOfficeLabel(selectedOfficeNumber)}
                            />
                        </Dropdown.Trigger>
                        <Dropdown.Content>
                            <Dropdown.Item
                                closeMenuOnSelect
                                isSelected={selectedOfficeNumber === ""}
                                onClick={() => setSelectedOfficeNumber("")}
                            >
                                Show all
                            </Dropdown.Item>
                            {sortStringsWithNumberEndings(
                                uniq(filteredOffices[selectedLocation] || [])
                            ).map((r) => (
                                <Dropdown.Item
                                    key={r}
                                    onClick={() => setSelectedOfficeNumber(r)}
                                    isSelected={selectedOfficeNumber === r}
                                >
                                    {r}
                                </Dropdown.Item>
                            ))}
                        </Dropdown.Content>
                    </Dropdown>
                    <Button
                        variant="normal"
                        className={styles.resetFiltersButton}
                        onClick={handleResetFilters}
                    >
                        Reset Filters
                    </Button>
                </div>
                <Button
                    onClick={() => {
                        setRequestOfficeDetails({
                            ...requestOfficeDetails,
                            show: true,
                        });
                    }}
                >
                    Set Availability
                </Button>
            </div>
            {officeWindows &&
                (officeWindows.length === 0 ? (
                    <EmptyListState
                        emptyMessage="No office windows"
                        stateHelperText="Office windows will be shown here"
                    />
                ) : (
                    <Table
                        stickyLastColumn
                        columns={columns}
                        data={validOfficeWindows || []}
                        allowSelect={false}
                        footer={tableFooter}
                    />
                ))}
        </div>
    );
}
